import React, {useContext, useEffect, useState, useCallback} from 'react'
import {Card, Col, Row, Button, Table, Badge, ProgressBar, Spinner, Alert, Modal} from 'react-bootstrap'
import {PageTitle} from '../../../_metronic/layout/core'
import {getAllSyncStatus, syncPushAll, syncPullAll, syncPush, syncPull} from '../generic/crud'
import moment from 'moment'
import BedRockContext from '../../BedRockContext'
import {formatDateTime} from '../../utils/Utils'

/**
 * SyncPage Component
 * Comprehensive sync status dashboard with push/pull capabilities
 */
const SyncPage = () => {
    const myData = useContext(BedRockContext)
    const [loading, setLoading] = useState(true)
    const [syncData, setSyncData] = useState(null)
    const [error, setError] = useState(null)
    const [lastUpdated, setLastUpdated] = useState(null)

    // Bulk sync states
    const [bulkSyncing, setBulkSyncing] = useState(false)
    const [bulkSyncType, setBulkSyncType] = useState(null)
    const [bulkSyncResult, setBulkSyncResult] = useState(null)
    const [showBulkModal, setShowBulkModal] = useState(false)

    // Individual entity sync states
    const [entitySyncing, setEntitySyncing] = useState({})

    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [selectedError, setSelectedError] = useState(null)

    // Reset toolbar buttons when entering this page
    useEffect(() => {
        myData.setBaseData({ toolBarButtons: [] })
    }, [])

    // Fetch sync status on component mount and every 30 seconds
    useEffect(() => {
        fetchSyncStatus()
        const interval = setInterval(fetchSyncStatus, 30000)
        return () => clearInterval(interval)
    }, [])

    // Fetch sync status from API
    const fetchSyncStatus = async () => {
        try {
            setLoading(true)
            setError(null)
            const response = await getAllSyncStatus()
            setSyncData(response.data)
            setLastUpdated(new Date())
        } catch (err) {
            console.error('Error fetching sync status:', err)
            setError('Failed to fetch sync status. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    // Push all entities to server
    const handlePushAll = async () => {
        setBulkSyncType('push')
        setBulkSyncResult(null)
        setBulkSyncing(true)
        setShowBulkModal(true)

        try {
            const response = await syncPushAll()
            setBulkSyncResult(response.data)
            await fetchSyncStatus()
        } catch (err) {
            console.error('Error pushing all:', err)
            setBulkSyncResult({
                success: false,
                message: 'Push failed: ' + (err.response?.data?.message || err.message)
            })
        } finally {
            setBulkSyncing(false)
        }
    }

    // Pull all entities from server
    const handlePullAll = async () => {
        setBulkSyncType('pull')
        setBulkSyncResult(null)
        setBulkSyncing(true)
        setShowBulkModal(true)

        try {
            const response = await syncPullAll()
            setBulkSyncResult(response.data)
            await fetchSyncStatus()
        } catch (err) {
            console.error('Error pulling all:', err)
            setBulkSyncResult({
                success: false,
                message: 'Pull failed: ' + (err.response?.data?.message || err.message)
            })
        } finally {
            setBulkSyncing(false)
        }
    }

    // Push single entity
    const handlePushEntity = async (entityId) => {
        setEntitySyncing(prev => ({...prev, [entityId]: 'push'}))
        try {
            await syncPush(entityId)
            await fetchSyncStatus()
        } catch (err) {
            console.error('Error pushing entity:', err)
        } finally {
            setEntitySyncing(prev => ({...prev, [entityId]: null}))
        }
    }

    // Pull single entity
    const handlePullEntity = async (entityId) => {
        setEntitySyncing(prev => ({...prev, [entityId]: 'pull'}))
        try {
            await syncPull(entityId)
            await fetchSyncStatus()
        } catch (err) {
            console.error('Error pulling entity:', err)
        } finally {
            setEntitySyncing(prev => ({...prev, [entityId]: null}))
        }
    }

    // Format date for display
    const formatDate = (date) => {
        if (!date) return 'Never'
        return formatDateTime(date)
    }

    // Format relative time
    const formatRelativeTime = (date) => {
        if (!date) return 'Never'
        return moment(date).fromNow()
    }

    // Get status badge color
    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case 'COMPLETED':
                return <Badge bg="success">Synced</Badge>
            case 'FAILED':
                return <Badge bg="danger">Failed</Badge>
            case 'IN_PROGRESS':
                return <Badge bg="warning">In Progress</Badge>
            default:
                return <Badge bg="secondary">Never Synced</Badge>
        }
    }

    // Get pending count badge
    const getPendingBadge = (count) => {
        if (count === 0) {
            return <Badge bg="success">All Synced</Badge>
        }
        return <Badge bg="warning">{count} Pending</Badge>
    }

    // Close bulk modal
    const closeBulkModal = () => {
        if (!bulkSyncing) {
            setShowBulkModal(false)
            setBulkSyncResult(null)
        }
    }

    // Close error modal
    const closeErrorModal = () => {
        setShowErrorModal(false)
        setSelectedError(null)
    }

    // Render summary cards
    const renderSummaryCards = () => {
        if (!syncData) return null

        return (
            <Row className="mb-5 g-4">
                <Col md={3}>
                    <Card className="h-100 bg-light-primary border border-primary border-dashed">
                        <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="symbol symbol-60px mb-4">
                                <span className="symbol-label bg-primary">
                                    <i className="fas fa-database text-white fs-1"></i>
                                </span>
                            </div>
                            <h2 className="fw-bold text-primary mb-1">{syncData.entityCount || 0}</h2>
                            <span className="text-muted fs-6 d-block text-center">Sync-Enabled<br/>Items</span>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="h-100 bg-light-success border border-success border-dashed">
                        <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="symbol symbol-60px mb-4">
                                <span className="symbol-label bg-success">
                                    <i className="fas fa-check-circle text-white fs-1"></i>
                                </span>
                            </div>
                            <h2 className="fw-bold text-success mb-1">{syncData.totalRecordCount || 0}</h2>
                            <span className="text-muted fs-6 d-block text-center">Total Records</span>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="h-100 bg-light-warning border border-warning border-dashed">
                        <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="symbol symbol-60px mb-4">
                                <span className="symbol-label bg-warning">
                                    <i className="fas fa-clock text-white fs-1"></i>
                                </span>
                            </div>
                            <h2 className="fw-bold text-warning mb-1">{syncData.totalPendingCount || 0}</h2>
                            <span className="text-muted fs-6 d-block text-center">Pending Sync</span>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="h-100 bg-light-info border border-info border-dashed">
                        <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="symbol symbol-60px mb-4">
                                <span className="symbol-label bg-info">
                                    <i className="fas fa-sync text-white fs-1"></i>
                                </span>
                            </div>
                            <h6 className="fw-bold text-info mb-1">{formatRelativeTime(lastUpdated)}</h6>
                            <span className="text-muted fs-6 d-block text-center">Last Refreshed</span>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        )
    }

    // Render bulk sync modal
    const renderBulkSyncModal = () => (
        <Modal show={showBulkModal} onHide={closeBulkModal} centered backdrop="static" size="lg">
            <Modal.Header closeButton={!bulkSyncing}>
                <Modal.Title>
                    <i className={`fas ${bulkSyncType === 'push' ? 'fa-cloud-upload-alt' : 'fa-cloud-download-alt'} me-2`}></i>
                    {bulkSyncing
                        ? (bulkSyncType === 'push' ? 'Pushing All Items...' : 'Pulling All Items...')
                        : (bulkSyncType === 'push' ? 'Push Complete' : 'Pull Complete')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {bulkSyncing ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" className="mb-3" style={{width: '4rem', height: '4rem'}} />
                        <h5 className="mb-3">
                            {bulkSyncType === 'push' ? 'Pushing data to server...' : 'Pulling data from server...'}
                        </h5>
                        <ProgressBar animated now={100} variant="primary" className="mt-3" />
                        <p className="text-muted mt-3">Please wait, this may take a moment.</p>
                    </div>
                ) : bulkSyncResult ? (
                    <div>
                        <Alert variant={bulkSyncResult.success ? 'success' : 'danger'} className="d-flex align-items-center">
                            <i className={`fas ${bulkSyncResult.success ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2 fs-3`}></i>
                            <div>
                                <strong>{bulkSyncResult.success ? 'Sync Successful!' : 'Sync Failed'}</strong>
                                <p className="mb-0 small">{bulkSyncResult.message}</p>
                            </div>
                        </Alert>

                        {bulkSyncResult.entities && bulkSyncResult.entities.length > 0 && (
                            <Table bordered hover className="mt-3 mb-0">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th className="text-center">Status</th>
                                        {bulkSyncType === 'push' ? (
                                            <>
                                                <th className="text-end">Processed</th>
                                                <th className="text-end">Success</th>
                                                <th className="text-end">Failed</th>
                                            </>
                                        ) : (
                                            <>
                                                <th className="text-end">Received</th>
                                                <th className="text-end">Inserted</th>
                                                <th className="text-end">Updated</th>
                                                <th className="text-end">Deleted</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {bulkSyncResult.entities.map((entity, idx) => (
                                        <tr key={idx}>
                                            <td>{entity.displayName}</td>
                                            <td className="text-center">
                                                {entity.success ? (
                                                    <i className="fas fa-check-circle text-success"></i>
                                                ) : (
                                                    <i className="fas fa-times-circle text-danger"></i>
                                                )}
                                            </td>
                                            {bulkSyncType === 'push' ? (
                                                <>
                                                    <td className="text-end">{entity.totalProcessed || 0}</td>
                                                    <td className="text-end text-success">{entity.successCount || 0}</td>
                                                    <td className="text-end text-danger">{entity.failedCount || 0}</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="text-end">{entity.totalCount || 0}</td>
                                                    <td className="text-end text-info">{entity.insertedCount || 0}</td>
                                                    <td className="text-end text-warning">{entity.updatedCount || 0}</td>
                                                    <td className="text-end text-danger">{entity.deletedCount || 0}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr className="fw-bold">
                                        <td>Total</td>
                                        <td></td>
                                        {bulkSyncType === 'push' ? (
                                            <>
                                                <td className="text-end">{bulkSyncResult.totalProcessed || 0}</td>
                                                <td className="text-end text-success">{bulkSyncResult.totalSuccess || 0}</td>
                                                <td className="text-end text-danger">{bulkSyncResult.totalFailed || 0}</td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="text-end">{bulkSyncResult.totalReceived || 0}</td>
                                                <td className="text-end text-info">{bulkSyncResult.totalInserted || 0}</td>
                                                <td className="text-end text-warning">{bulkSyncResult.totalUpdated || 0}</td>
                                                <td className="text-end text-danger">{bulkSyncResult.totalDeleted || 0}</td>
                                            </>
                                        )}
                                    </tr>
                                </tfoot>
                            </Table>
                        )}

                        {bulkSyncResult.syncTime && (
                            <p className="text-muted small mt-3 mb-0 text-center">
                                <i className="fas fa-clock me-1"></i>
                                Completed at: {formatDate(bulkSyncResult.syncTime)}
                            </p>
                        )}
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeBulkModal} disabled={bulkSyncing}>
                    {bulkSyncing ? 'Please wait...' : 'Close'}
                </Button>
            </Modal.Footer>
        </Modal>
    )

    // Render error modal
    const renderErrorModal = () => (
        <Modal show={showErrorModal} onHide={closeErrorModal} centered>
            <Modal.Header closeButton className="bg-light-danger">
                <Modal.Title className="text-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Sync Error
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {selectedError && (
                    <div>
                        <div className="mb-3">
                            <label className="text-muted small mb-1">Item</label>
                            <p className="fw-bold mb-0">{selectedError.collectionName}</p>
                        </div>
                        {selectedError.lastAttemptTime && (
                            <div className="mb-3">
                                <label className="text-muted small mb-1">Last Attempt</label>
                                <p className="mb-0">{formatDate(selectedError.lastAttemptTime)}</p>
                            </div>
                        )}
                        <div className="mb-0">
                            <label className="text-muted small mb-1">Error Message</label>
                            <Alert variant="danger" className="mb-0">
                                <i className="fas fa-bug me-2"></i>
                                {selectedError.errorMessage}
                            </Alert>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeErrorModal}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    )

    // Render entity table
    const renderEntityTable = () => {
        if (!syncData || !syncData.entities) return null

        return (
            <Card className="mb-5">
                <Card.Header className="d-flex justify-content-between align-items-center px-6 py-4">
                    <h4 className="card-title m-0">
                        <i className="fas fa-list me-2"></i>
                        Item Status
                    </h4>
                    <Button variant="outline-primary" size="sm" onClick={fetchSyncStatus} disabled={loading}>
                        <i className={`fas fa-sync me-1 ${loading ? 'fa-spin' : ''}`}></i>
                        Refresh
                    </Button>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th className="ps-6 py-4" style={{width: '22%'}}>Item</th>
                                <th className="text-center py-4" style={{width: '10%'}}>Sync Mode</th>
                                <th className="text-center py-4" style={{width: '10%'}}>Total Records</th>
                                <th className="text-center py-4" style={{width: '10%'}}>Pending</th>
                                <th className="text-center py-4" style={{width: '10%'}}>Status</th>
                                <th className="py-4" style={{width: '18%'}}>Last Sync</th>
                                <th className="text-end pe-6 py-4" style={{width: '20%'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {syncData.entities.map((entity, idx) => (
                                <tr key={idx}>
                                    <td className="ps-6 py-4">
                                        <div className="d-flex align-items-center">
                                            <div className="symbol symbol-40px me-3">
                                                <span className="symbol-label bg-light-primary">
                                                    <i className="fas fa-table text-primary fs-5"></i>
                                                </span>
                                            </div>
                                            <div>
                                                <span className="fw-bold">{entity.displayName}</span>
                                                <br />
                                                <small className="text-muted">{entity.collectionName}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-center py-4">
                                        <div className="d-flex flex-column align-items-center gap-1">
                                            {entity.pushEnabled && entity.pullEnabled ? (
                                                <Badge bg="primary">Push & Pull</Badge>
                                            ) : entity.pushEnabled ? (
                                                <Badge bg="info">Push Only</Badge>
                                            ) : entity.pullEnabled ? (
                                                <Badge bg="success">Pull Only</Badge>
                                            ) : (
                                                <Badge bg="secondary">None</Badge>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-center py-4">
                                        <span className="fw-bold fs-5">{entity.totalCount || 0}</span>
                                    </td>
                                    <td className="text-center py-4">
                                        {getPendingBadge(entity.pendingCount || 0)}
                                    </td>
                                    <td className="text-center py-4">
                                        <div className="d-flex flex-column align-items-center">
                                            {entity.syncStatus?.toUpperCase() === 'FAILED' && entity.lastErrorMessage ? (
                                                <Badge
                                                    bg="danger"
                                                    style={{cursor: 'pointer'}}
                                                    onClick={() => {
                                                        setSelectedError({
                                                            collectionName: entity.displayName,
                                                            errorMessage: entity.lastErrorMessage,
                                                            lastAttemptTime: entity.lastAttemptTime
                                                        })
                                                        setShowErrorModal(true)
                                                    }}
                                                >
                                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                                    Failed
                                                </Badge>
                                            ) : (
                                                getStatusBadge(entity.syncStatus)
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-4">
                                        <span>{formatRelativeTime(entity.lastSyncTime)}</span>
                                        <br />
                                        <small className="text-muted">{formatDate(entity.lastSyncTime)}</small>
                                    </td>
                                    <td className="text-end pe-6 py-4">
                                        <div className="d-flex justify-content-end gap-2">
                                            {entity.pushEnabled !== false && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline btn-outline-info btn-active-light-info"
                                                    onClick={() => handlePushEntity(entity.entityId)}
                                                    disabled={entitySyncing[entity.entityId]}
                                                >
                                                    {entitySyncing[entity.entityId] === 'push' ? (
                                                        <Spinner size="sm" animation="border" />
                                                    ) : (
                                                        <i className="fas fa-cloud-upload-alt me-1"></i>
                                                    )}
                                                    Push
                                                </button>
                                            )}
                                            {entity.pullEnabled !== false && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline btn-outline-success btn-active-light-success"
                                                    onClick={() => handlePullEntity(entity.entityId)}
                                                    disabled={entitySyncing[entity.entityId]}
                                                >
                                                    {entitySyncing[entity.entityId] === 'pull' ? (
                                                        <Spinner size="sm" animation="border" />
                                                    ) : (
                                                        <i className="fas fa-cloud-download-alt me-1"></i>
                                                    )}
                                                    Pull
                                                </button>
                                            )}
                                            {entity.pushEnabled === false && entity.pullEnabled === false && (
                                                <span className="text-muted small">No actions</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        )
    }

    return (
        <>
            <PageTitle>Sync Center</PageTitle>
            <div className="px-4 px-lg-8">
                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Header Card with Bulk Actions */}
                <Card className="mb-5">
                    <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-center px-6 py-5">
                        <div className="mb-4 mb-md-0">
                            <h2 className="fs-1 mb-1">
                                <i className="fas fa-sync-alt text-primary me-2"></i>
                                Data Synchronization
                            </h2>
                            <p className="text-muted mb-0">
                                Manage data sync between this client and the central server
                            </p>
                            {lastUpdated && (
                                <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    Last updated: {formatDate(lastUpdated)}
                                </small>
                            )}
                        </div>
                        <div className="d-flex gap-3">
                            <Button
                                variant="info"
                                size="lg"
                                onClick={handlePushAll}
                                disabled={bulkSyncing || loading}
                                className="d-flex align-items-center"
                            >
                                <i className="fas fa-cloud-upload-alt me-2 fs-3"></i>
                                <div className="text-start">
                                    <span className="d-block fw-bold">Push All</span>
                                    <small className="d-block opacity-75">Upload to Server</small>
                                </div>
                            </Button>
                            <Button
                                variant="success"
                                size="lg"
                                onClick={handlePullAll}
                                disabled={bulkSyncing || loading}
                                className="d-flex align-items-center"
                            >
                                <i className="fas fa-cloud-download-alt me-2 fs-3"></i>
                                <div className="text-start">
                                    <span className="d-block fw-bold">Pull All</span>
                                    <small className="d-block opacity-75">Download from Server</small>
                                </div>
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                {/* Loading State */}
                {loading && !syncData && (
                    <div className="text-center py-10">
                        <Spinner animation="border" variant="primary" className="mb-3" style={{width: '3rem', height: '3rem'}} />
                        <p className="text-muted">Loading sync status...</p>
                    </div>
                )}

                {/* Summary Cards */}
                {renderSummaryCards()}

                {/* Entity Table */}
                {renderEntityTable()}

                {/* Info Card */}
                <Card>
                    <Card.Header className="px-6 py-4">
                        <h4 className="card-title m-0">
                            <i className="fas fa-info-circle me-2"></i>
                            About Synchronization
                        </h4>
                    </Card.Header>
                    <Card.Body className="px-6 py-5">
                        <Row className="g-5">
                            <Col md={6}>
                                <div className="border border-primary border-dashed rounded p-5 h-100">
                                    <h5 className="text-primary mb-4">
                                        <i className="fas fa-cloud-upload-alt me-2"></i>
                                        Push to Server
                                    </h5>
                                    <p className="text-muted mb-3">
                                        Sends your data from this device to the main office server so it is safely backed up and available to others.
                                    </p>
                                    <ul className="text-muted mb-0">
                                        <li className="mb-2">Any new sales, purchases, or changes you made on this device will be sent to the server</li>
                                        <li className="mb-2">Items showing <Badge bg="warning">Pending</Badge> have not been sent yet</li>
                                        <li>Once sent successfully, items are marked as synced</li>
                                    </ul>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="border border-success border-dashed rounded p-5 h-100">
                                    <h5 className="text-success mb-4">
                                        <i className="fas fa-cloud-download-alt me-2"></i>
                                        Pull from Server
                                    </h5>
                                    <p className="text-muted mb-3">
                                        Gets the latest data from the main office server so this device stays up to date with any changes made elsewhere.
                                    </p>
                                    <ul className="text-muted mb-0">
                                        <li className="mb-2">Your unsent changes are pushed first so nothing is lost</li>
                                        <li className="mb-2">New customers, vehicles, products, or prices added on the server are downloaded to this device</li>
                                        <li>Any updates made on the server (e.g. corrected names, prices) will be applied here</li>
                                    </ul>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Bulk Sync Modal */}
                {renderBulkSyncModal()}

                {/* Error Modal */}
                {renderErrorModal()}
            </div>
        </>
    )
}

export {SyncPage}
